import React, { useCallback, useEffect } from "react";
import { Typography, Box } from "@mui/material";
import Button from "../../common/Button";
import { Save, PaymentsOutlined } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import {
  fetchActiveBillers,
  usePracticeApproveSlice,
} from "../../../redux/slice/practice/practiceApproveSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  PracticeBillerInfo,
  fetchPracticeBillerData,
  updatePracticeBillerById,
  usePracticeBillerSlice,
} from "../../../redux/slice/practice/practiceBillerSlice";
import { usePracticeEditSlice } from "../../../redux/slice/practice/practiceEditSlice";
import {
  MainErrorInfo,
  SingleAlertInfo,
  setMainErrorObj,
  setSingleAlertObj,
} from "../../../redux/slice/commonSlice";
import OptionsPopup from "../../common/OptionsPopup";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { MSG_BILLER_NOT_FOUND, MSG_NOT_AN_ACTIVE_BILLER, MSG_PRACTICE_BILLER_NOT_FOUND, USER_ROLE_ENTITY_NAMES } from "../../../constants/applicationConstants";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "../../../hooks/useNavigation";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { SectionHeader } from "../../ui/ContentSection";
import { useModalStyles } from "../../common/useModalStyles";

interface BillerInfoProps {
  contentChangedStatus: () => void;
}

const BillerInfoEditViewForm: React.FC<BillerInfoProps> = ({ contentChangedStatus }) => {
  const { control, handleSubmit, reset } = useForm<PracticeBillerInfo>();
  const { billerList } = usePracticeApproveSlice();
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const { practiceBillerInfo } = usePracticeBillerSlice();
  const [isBillerInfoVisible, setIsBillerInfoVisible] = React.useState(false);
  const billerOptions: any[] = [{ value: "", label: "Select" }];
  const { practiceEditInfo } = usePracticeEditSlice();
  const [optionPopupShow, setOptionPopupShow] = React.useState<{
    message: string;
    show: boolean;
  }>({ message: "", show: false });
  const { userInfo } = useAuthSlice();
  const {
    tokens: mTokens,
    isDark: isDarkModal,
    primaryBtnSx,
    inputFieldSx,
  } = useModalStyles();
  const cardSx = {
    borderRadius: "16px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: mTokens.bg,
    backgroundImage: "none",
    overflow: "hidden" as const,
    boxShadow: isDarkModal ? "0 10px 20px rgba(0,0,0,0.1)" : "none",
  };
  const saveBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    height: "42px",
    minHeight: "42px",
    maxHeight: "42px",
    py: 0,
    px: 2,
    boxSizing: "border-box",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700, lineHeight: 1 },
    "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
    "&.Mui-disabled": { opacity: 0.45, color: "#fff", "& .MuiTypography-root": { color: "#fff" } },
  };
  const dropdownFieldWrapSx = {
    ...inputFieldSx(),
    width: "100%",
    display: "flex",
    "& .MuiAutocomplete-root, & .MuiFormControl-root, & .MuiTextField-root": {
      width: "100%",
      maxWidth: "100%",
      // CustomDropdownField applies a 53px minHeight to the field root, which left an
      // 11px gap under the 42px input and pushed the Save button out of alignment.
      minHeight: "42px !important",
      height: "42px !important",
      margin: 0,
    },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root": {
      backgroundColor: `${mTokens.inputBg} !important`,
      borderRadius: "10px !important",
      minHeight: "42px !important",
      height: "42px !important",
      maxHeight: "42px !important",
      boxSizing: "border-box !important",
      alignItems: "center",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
      padding: "0 38px 0 12px !important",
      flexWrap: "nowrap !important",
      overflow: "hidden !important",
    },
    "& .MuiInputBase-input, & .MuiOutlinedInput-input, & .MuiAutocomplete-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiChip-root": {
      height: "24px !important",
      maxHeight: "24px !important",
    },
    "& .MuiInputLabel-root, & label": { display: "none" },
    "& .MuiAutocomplete-endAdornment": {
      top: "50% !important",
      transform: "translateY(-50%)",
      right: "8px !important",
    },
  };

  const defaultValues = useCallback(() => {
    if (practiceBillerInfo) {
      reset(practiceBillerInfo);
    }
  }, [practiceBillerInfo, reset]);

  useEffect(() => {
    defaultValues();
  }, [defaultValues]);

  useEffect(() => {
    dispatch(fetchActiveBillers());
    if (practiceEditInfo) {
      dispatch(fetchPracticeBillerData(practiceEditInfo.practiceId));
    }
  }, [dispatch, practiceEditInfo]);

  billerList?.map((biller) => {
    billerOptions.push({ value: biller.billerId, label: biller.billerName });
  });

  const confirmOptionPopup = () => {
    handleSubmit(handleUpdateBiller)();
    setOptionPopupShow({ message: "", show: false });
  };

  const cancelOptionPopup = () => {
    setOptionPopupShow({ message: "", show: false });
  };

  const handleUpdateBiller = async (practiceBillerData: any) => {
    let errorObj: MainErrorInfo = {
      title: "",
    };
    //CS-1348 - Start
    const data = {
      practiceBillerId: practiceBillerData.practiceBillerId ? practiceBillerData.practiceBillerId : 0, // Default to 0 if not provided
      billerId: practiceBillerData.biller.billerId ? practiceBillerData.biller.billerId : 0, // Default to 0 if not provided
      practiceId: practiceEditInfo?.practiceId,
    };
    //CS-1348 - End

    try {
      const response = await dispatch(updatePracticeBillerById(data));
      if (response.payload && response.payload.message === "SUCCESS") {
        setIsBillerInfoVisible(false);
        let successObj: SingleAlertInfo = {
          message: "Practice Biller Updated Successfully.",
          alertType: "success",
        };
        dispatch(setSingleAlertObj(successObj));
        contentChangedStatus();
      } else {
        let errorSentence = "";
        if (response.payload && response.payload.errors.length > 0) {
          response.payload.errors.forEach((error: any, index: any) => {
            if (error.field) {
              errorSentence += `${error.field}: ${error.errorMessage}\n`;
            } else {
              errorSentence += `${error.reason}\n`;
            }
          });
        } else if (response.payload && response.payload.message) {
          switch (response.payload.message) {
            case MSG_PRACTICE_BILLER_NOT_FOUND:
              errorSentence = "No Practice Biller Found. Please check their status and try again.";
              break;
            case MSG_NOT_AN_ACTIVE_BILLER:
              errorSentence = "Given Practice Biller is not active. Please check their status and try again.";
              break;
            case MSG_BILLER_NOT_FOUND:
              errorSentence = "No Biller Found.";
              break;
            default:
              errorSentence = "An unknown error occurred.";
          }
        }
        else {
          errorSentence = "An unknown error occurred.";
        }

        errorObj.title = "Updating Practice Biller Failed!";
        errorObj.errorMessageStream = errorSentence;
        dispatch(setMainErrorObj(errorObj));
        navigate("/practice_list");
      }
    } catch (error) {
      errorObj.title = "Error Updating practice biller";
      errorObj.errorMessageStream = "" + error;
      dispatch(setMainErrorObj(errorObj));
    }
  };

  //CS-1348 - Start
  const handleChange = (e: any) => {    
    let value;
    if (e && e.value !== undefined) {
      value = e.value;
    } else {
      value = "";
    }

    // If value is same as the current biller ID, disable the save button
    if (value === practiceBillerInfo?.biller?.billerId) {
      setIsBillerInfoVisible(false);
    } else if (value === "" && !practiceBillerInfo?.biller?.billerId) {
      // If value empty and no existing practiceBillerId, disable
      setIsBillerInfoVisible(false);
    } 
    else {
      setIsBillerInfoVisible(true);
    }
  };
  //CS-1348 - End

  return (
    <Box sx={cardSx}>
      <SectionHeader
        icon={<PaymentsOutlined fontSize="small" />}
        title="Biller Information"
        subtitle="Select the biller assigned to this practice."
      />
      <Box sx={{ px: { xs: 1.5, md: 2 }, py: 1.25 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-end" },
          gap: 1.5,
        }}
      >
        <Box sx={{ width: { xs: "100%", sm: "auto" }, flex: { sm: "1 1 320px" }, maxWidth: { sm: 420 } }}>
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mTokens.textSecondary, mb: 0.5 }}>Practice Biller</Typography>
          <Box sx={dropdownFieldWrapSx}>
          <CustomDropdownField
            control={control}
            name={`biller.${"billerId"}`}
            options={billerOptions}
            label="Practice Biller"
            onChangeSelect={handleChange}
            disabled={!(userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN)}
          />
          </Box>
        </Box>
        <Box sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}>
          <form onSubmit={handleSubmit(handleUpdateBiller)}>
              <Button
                onClick={() => {
                  setOptionPopupShow({
                    message:
                      "Do you really want to update practice biller details? ",
                    show: true,
                  });
                }}
                disabled={!isBillerInfoVisible}
                startIcon={<Save />}
                removeUpperCase
                sx={{ ...saveBtnSx, width: { xs: "100%", sm: "auto" } }}
              >
                Save
              </Button>
          </form>
        </Box>
      </Box>
      </Box>
      <OptionsPopup
        open={optionPopupShow.show}
        onClose={() => setOptionPopupShow({ message: "", show: false })}
        variant="confirm"
        message={<>{optionPopupShow.message}</>}
        buttons={[
          { name: "Confirm", color: "primary", onClick: confirmOptionPopup },
          { name: "Cancel", color: "secondary", onClick: cancelOptionPopup },
        ]}
      />
    </Box>
  );
};

export default BillerInfoEditViewForm;
